import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft, ArrowRight, Code2, Flame, Trophy, Zap, BookOpen, Lock,
  CheckCircle2, Loader2, Sparkles, Brain, Terminal, Star,
  Home, Map, Image as ImageIcon, LogOut, Menu as MenuIcon, X, Play
} from "lucide-react";
import { getStoredUser, getAuthConfig, loginWithGoogle, clearStoredUser } from "../lib/userAuth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function StatCard({ icon: Icon, label, value, accent = "#00F0FF", testId }) {
  return (
    <div data-testid={testId} className="bg-zinc-900/60 border border-zinc-800 rounded-xl px-5 py-4 flex items-center gap-4">
      <div
        className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${accent}20`, color: accent }}
      >
        <Icon size={20} />
      </div>
      <div>
        <div className="text-2xl font-outfit font-bold text-white leading-none">{value}</div>
        <div className="text-xs text-zinc-500 font-dm-sans uppercase tracking-wide mt-1">{label}</div>
      </div>
    </div>
  );
}

function ModuleCard({ module, onOpen, prereqLocked, prereqTitle }) {
  const pct = module.progress_pct || 0;
  const completed = module.lessons_completed || 0;
  const total = module.lessons_total || 0;
  // Backend now returns `is_locked: true` for paywalled modules when user lacks Pro Pass
  const premiumLocked = !!module.is_locked;
  const locked = prereqLocked || premiumLocked;

  return (
    <motion.div
      whileHover={locked ? {} : { y: -4 }}
      transition={{ duration: 0.2 }}
      onClick={() => { if (!locked) onOpen(); }}
      data-testid={`module-card-${module.slug}`}
      data-locked={locked ? "true" : "false"}
      aria-disabled={locked}
      className={`relative bg-zinc-900/60 border rounded-2xl overflow-hidden transition-colors ${
        locked
          ? "border-zinc-900 opacity-50 grayscale cursor-not-allowed select-none"
          : "border-zinc-800 cursor-pointer hover:border-[#00F0FF]/40"
      }`}
    >
      {prereqLocked && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <div className="bg-black/70 backdrop-blur-sm border border-zinc-700 rounded-full px-4 py-2 flex items-center gap-2">
            <Lock size={14} className="text-zinc-300" />
            <span className="text-xs font-mono font-bold text-zinc-200 uppercase tracking-wide">Locked</span>
          </div>
        </div>
      )}
      {premiumLocked && !prereqLocked && (
        <a href="/pricing#python-lab"
           onClick={(e) => e.stopPropagation()}
           className="absolute inset-0 z-10 flex items-center justify-center"
           data-testid={`unlock-pro-${module.slug}`}>
          <div className="bg-black/80 backdrop-blur-sm border border-amber-400/40 rounded-full px-4 py-2 flex items-center gap-2 hover:border-amber-400/80 hover:bg-black/90 transition-colors">
            <Lock size={14} className="text-amber-300" />
            <span className="text-xs font-mono font-bold text-amber-200 uppercase tracking-wide">Unlock with Pro Pass</span>
          </div>
        </a>
      )}
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono font-bold text-[#00F0FF] bg-[#00F0FF]/10 px-2 py-0.5 rounded">
              MODULE {String(module.order || 0).padStart(2, "0")}
            </span>
            {(module.is_premium || module.is_locked) && (
              <span className="text-[10px] font-bold text-amber-300 bg-amber-300/10 px-2 py-0.5 rounded inline-flex items-center gap-1">
                <Lock size={10} /> Pro Pass
              </span>
            )}
          </div>
          <div className="text-xs text-zinc-500 font-dm-sans">{total} lessons</div>
        </div>
        <h3 className="text-xl font-outfit font-bold text-white mb-2">{module.title}</h3>
        <p className="text-sm text-zinc-400 font-dm-sans mb-5 line-clamp-2">{module.summary}</p>

        {/* Progress bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs font-dm-sans">
            <span className="text-zinc-500">Progress</span>
            <span className="text-white font-bold">{completed}/{total} · {pct}%</span>
          </div>
          <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#00F0FF] to-[#7000FF] transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <div className="mt-5 flex items-center gap-2 text-sm font-dm-sans font-bold group">
          {prereqLocked ? (
            <span className="text-zinc-400 inline-flex items-center gap-1.5">
              <Lock size={13} /> Finish "{prereqTitle}" to unlock
            </span>
          ) : (
            <span className="text-[#00F0FF] inline-flex items-center gap-1.5">
              {pct === 0 ? "Start module" : pct === 100 ? "Review" : "Continue"}
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function NavMenuItem({ icon: Icon, label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white hover:bg-zinc-900 px-3 py-2 rounded-lg text-sm font-dm-sans font-medium transition-colors"
    >
      <Icon size={14} /> {label}
    </button>
  );
}

function MobileMenuItem({ icon: Icon, label, onClick, testId, danger }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full inline-flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-dm-sans font-medium transition-colors ${
        danger
          ? "text-red-300 hover:bg-red-500/10"
          : "text-zinc-300 hover:bg-zinc-900 hover:text-white"
      }`}
    >
      <Icon size={15} /> {label}
    </button>
  );
}

function LoginGate({ onLogin, onTry }) {
  return (
    <div className="min-h-screen bg-[#050505] grid-bg flex items-center justify-center px-6 py-20">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full bg-zinc-900/80 border border-zinc-800 backdrop-blur-xl rounded-2xl p-10 text-center"
      >
        <div className="w-16 h-16 bg-gradient-to-br from-[#00F0FF] to-[#7000FF] rounded-2xl flex items-center justify-center mx-auto mb-6">
          <Terminal size={28} className="text-black" />
        </div>
        <h1 className="font-outfit font-black text-3xl text-white mb-3">Welcome to Python Lab</h1>
        <p className="text-zinc-400 text-sm font-dm-sans mb-8 leading-relaxed">
          Learn Python by writing real code in your browser. Sign in with Google to track your progress, earn XP, and unlock the AI tutor.
        </p>
        <button
          data-testid="python-lab-login-btn"
          onClick={onLogin}
          className="w-full bg-white text-black font-outfit font-bold py-3.5 rounded-lg hover:bg-zinc-100 transition-colors flex items-center justify-center gap-3"
        >
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continue with Google
        </button>
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-zinc-800" />
          <span className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest">or</span>
          <div className="flex-1 h-px bg-zinc-800" />
        </div>
        <button
          data-testid="python-lab-try-btn"
          onClick={onTry}
          className="w-full bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-[#00F0FF]/50 text-white font-outfit font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <Play size={15} className="text-[#00F0FF]" />
          Try a free lesson — no signup
        </button>
        <p className="text-xs text-zinc-600 mt-5 font-dm-sans">
          We use Google sign-in to save your progress. We never post on your behalf.
        </p>
      </motion.div>
    </div>
  );
}

export default function PythonLabPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [progress, setProgress] = useState([]);
  const [continueLesson, setContinueLesson] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!user?.session_token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const cfg = getAuthConfig();
      const [statsRes, progRes, contRes] = await Promise.all([
        axios.get(`${API}/python-lab/me/stats`, cfg).catch(() => null),
        axios.get(`${API}/python-lab/me/progress`, cfg).catch(() => null),
        axios.get(`${API}/python-lab/me/continue`, cfg).catch(() => null),
      ]);
      if (statsRes) setStats(statsRes.data);
      if (progRes) setProgress(progRes.data);
      if (contRes?.data?.lesson) setContinueLesson(contRes.data);
    } catch (e) {
      // If 401, force re-login
      if (e?.response?.status === 401) {
        clearStoredUser();
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (!user?.session_token) {
    return (
      <LoginGate
        onLogin={() => loginWithGoogle("/ai-tools/python-lab")}
        onTry={() => navigate("/ai-tools/python-lab/try")}
      />
    );
  }

  const handleSignOut = () => {
    clearStoredUser();
    setUser(null);
    navigate("/");
  };

  const handleHome = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-zinc-900 sticky top-0 z-30 bg-[#050505]/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <button
            data-testid="back-to-tools"
            onClick={() => navigate("/ai-tools")}
            className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-dm-sans transition-colors shrink-0"
          >
            <ArrowLeft size={16} /> AI Tools
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1 ml-2" aria-label="Python Lab navigation">
            <NavMenuItem icon={Home} label="Home" testId="nav-home" onClick={handleHome} />
            <NavMenuItem icon={Map} label="Curriculum Roadmap" testId="nav-roadmap"
                         onClick={() => navigate("/ai-tools/python-lab/roadmap")} />
            <NavMenuItem icon={ImageIcon} label="Public Showcase" testId="nav-showcase"
                         onClick={() => navigate("/showcase/python-lab")} />
          </nav>

          {/* Right cluster: identity + sign out */}
          <div className="flex items-center gap-3">
            <div className="text-right hidden lg:block">
              <div className="text-xs text-zinc-500 font-dm-sans leading-none">Signed in</div>
              <div className="text-sm text-white font-dm-sans font-medium leading-none mt-1 max-w-[180px] truncate">{user.email}</div>
            </div>
            {user.picture && (
              <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full border border-zinc-700 hidden sm:block" />
            )}
            <button
              data-testid="nav-signout"
              onClick={handleSignOut}
              className="hidden md:inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-red-500/40 hover:text-red-300 text-zinc-300 text-xs font-dm-sans font-bold px-3 py-2 rounded-lg transition-colors"
            >
              <LogOut size={13} /> Sign out
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="md:hidden w-9 h-9 flex items-center justify-center text-zinc-300 bg-zinc-900 border border-zinc-800 rounded-lg"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? <X size={16} /> : <MenuIcon size={16} />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden border-t border-zinc-900 bg-[#070710]"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-2">
              <MobileMenuItem icon={Home} label="Home" testId="m-nav-home"
                              onClick={() => { handleHome(); setMobileMenuOpen(false); }} />
              <MobileMenuItem icon={Map} label="Curriculum Roadmap" testId="m-nav-roadmap"
                              onClick={() => { navigate("/ai-tools/python-lab/roadmap"); setMobileMenuOpen(false); }} />
              <MobileMenuItem icon={ImageIcon} label="Public Showcase" testId="m-nav-showcase"
                              onClick={() => { navigate("/showcase/python-lab"); setMobileMenuOpen(false); }} />
              <div className="border-t border-zinc-800 my-2" />
              <MobileMenuItem icon={LogOut} label="Sign out" testId="m-nav-signout"
                              onClick={handleSignOut} danger />
            </div>
          </motion.div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 space-y-12">
        {/* Hero */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2 bg-gradient-to-br from-[#00F0FF]/10 via-zinc-900/40 to-[#7000FF]/10 border border-zinc-800 rounded-2xl p-8 relative overflow-hidden"
          >
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-full px-3 py-1 text-xs font-mono text-[#00F0FF] mb-4">
                <Sparkles size={12} /> PYTHON LAB · Phase 1
              </div>
              <h1 className="font-outfit font-black text-4xl sm:text-5xl text-white leading-tight">
                Stop watching.<br />
                <span className="bg-gradient-to-r from-[#00F0FF] to-[#7000FF] bg-clip-text text-transparent">Start coding.</span>
              </h1>
              <p className="text-zinc-400 mt-4 max-w-xl font-dm-sans text-base leading-relaxed">
                Real Python in your browser. Auto-graded lessons. An AI tutor (Claude Sonnet 4.5) that gives you Socratic hints — never spoils the answer.
              </p>
              {continueLesson?.lesson && (
                <button
                  data-testid="continue-lesson-btn"
                  onClick={() => navigate(`/ai-tools/python-lab/lesson/${continueLesson.lesson.slug}`)}
                  className="mt-6 inline-flex items-center gap-2 bg-[#00F0FF] text-black font-outfit font-bold px-5 py-3 rounded-lg hover:bg-white transition-colors"
                >
                  Continue: {continueLesson.lesson.title}
                  <ArrowRight size={16} />
                </button>
              )}
              {!continueLesson?.lesson && progress.length > 0 && progress[0].lessons_total > 0 && (
                <button
                  data-testid="start-learning-btn"
                  onClick={() => navigate(`/ai-tools/python-lab/module/${progress[0].slug}`)}
                  className="mt-6 inline-flex items-center gap-2 bg-[#00F0FF] text-black font-outfit font-bold px-5 py-3 rounded-lg hover:bg-white transition-colors"
                >
                  Start your first lesson <ArrowRight size={16} />
                </button>
              )}
            </div>
            <div className="absolute -right-12 -top-10 w-72 h-72 bg-[#00F0FF]/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -right-20 -bottom-20 w-96 h-96 bg-[#7000FF]/10 rounded-full blur-3xl pointer-events-none" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="grid grid-cols-2 gap-3"
          >
            <StatCard icon={Trophy} label="XP" value={stats?.xp ?? 0} accent="#FFC857" testId="stat-xp" />
            <StatCard icon={Star} label="Level" value={stats?.level ?? 1} accent="#7000FF" testId="stat-level" />
            <StatCard icon={Flame} label="Streak" value={`${stats?.streak_current ?? 0}d`} accent="#FF6B6B" testId="stat-streak" />
            <StatCard icon={CheckCircle2} label="Lessons" value={`${stats?.lessons_completed ?? 0}/${stats?.lessons_total ?? 0}`} accent="#3DDC84" testId="stat-lessons" />
          </motion.div>
        </section>

        {/* Modules */}
        <section>
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="font-outfit font-bold text-2xl text-white">Curriculum</h2>
              <p className="text-zinc-500 text-sm font-dm-sans mt-1">
                {progress.length || 30} modules · {progress.reduce((sum, m) => sum + (m.lessons_total || 0), 0) || 600} lessons across Basics → AI → Pro+
              </p>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={28} className="text-[#00F0FF] animate-spin" />
            </div>
          ) : progress.length === 0 ? (
            <div className="text-center py-16 bg-zinc-900/40 border border-zinc-800 rounded-2xl">
              <BookOpen size={36} className="text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400 font-dm-sans">No modules yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {(() => {
                // Lock rule: Module N unlocks only when ALL previous modules are 100% complete.
                // Module 1 is always unlocked. Premium-locking still applies independently.
                const sorted = [...progress].sort((a, b) => (a.order || 0) - (b.order || 0));
                let prereqMet = true;
                let prevTitle = "";
                return sorted.map((m) => {
                  const node = (
                    <ModuleCard
                      key={m.module_id}
                      module={m}
                      onOpen={() => navigate(`/ai-tools/python-lab/module/${m.slug}`)}
                      prereqLocked={!prereqMet}
                      prereqTitle={prevTitle}
                    />
                  );
                  // After this module is rendered, decide whether the NEXT one is unlocked
                  if ((m.progress_pct || 0) < 100 || (m.lessons_total || 0) === 0) {
                    prereqMet = false;
                    if (!prevTitle) prevTitle = m.title;
                  }
                  return node;
                });
              })()}
            </div>
          )}
        </section>

        {/* Why Python Lab */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-5 pb-10">
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-2xl p-6">
            <Brain className="text-[#00F0FF] mb-3" size={22} />
            <h3 className="font-outfit font-bold text-white mb-2">AI Tutor (Claude 4.5)</h3>
            <p className="text-sm text-zinc-400 font-dm-sans leading-relaxed">Four hint levels — from a Socratic question to a guided solution. Stuck? You'll never stay stuck.</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-2xl p-6">
            <Code2 className="text-[#3DDC84] mb-3" size={22} />
            <h3 className="font-outfit font-bold text-white mb-2">Real Python in Your Browser</h3>
            <p className="text-sm text-zinc-400 font-dm-sans leading-relaxed">No installs. No sandbox setup. Pyodide runs Python 3.12 right in your browser via WebAssembly.</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-2xl p-6">
            <Zap className="text-[#FFC857] mb-3" size={22} />
            <h3 className="font-outfit font-bold text-white mb-2">Auto-Graded Lessons</h3>
            <p className="text-sm text-zinc-400 font-dm-sans leading-relaxed">Visible + hidden tests grade your work in milliseconds. PASS, PARTIAL, FAIL — with rich feedback.</p>
          </div>
        </section>
      </main>
    </div>
  );
}
